import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tab,
    Tabs,
    IconButton,
    Tooltip
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import { API_BASE_URL } from '../../services/apiConfig';
import { fetchWithAuth } from '../../services/authService';

interface AdminStats {
    totalUsers: number;
    totalWorks: number;
    totalActivities: number;
}

interface User {
    userId: string;
    email: string;
    ragione_sociale?: string;
    createdAt: string;
    subscriptionStatus?: string;
    activitiesCount: number;
}

interface Work {
    id_work: string;
    work_name: string;
    status: string;
    completion_percentage: number;
    created_at: string;
    id_user: string;
}

interface Payment {
    paymentId: string;
    userId: string;
    userEmail: string;
    amount: number;
    mode: string;
    stripeStatus: string;
    paidAt: string;
}

interface PaymentStats {
    totalPayments: number;
    totalRevenue: number;
    paidCount: number;
    failedCount: number;
    averageAmount: string;
}

export const AdminDashboard: React.FC = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [works, setWorks] = useState<Work[]>([]);
    const [payments, setPayments] = useState<Payment[]>([]);
    const [paymentStats, setPaymentStats] = useState<PaymentStats | null>(null);

    useEffect(() => {
        fetchAdminData();
    }, []);

    // ✅ SICURO: Usa fetchWithAuth che aggiunge Authorization: Bearer <AccessToken>
    const fetchAdminData = async () => {
        try {
            
            // Fetch stats
            const statsRes = await fetchWithAuth(`${API_BASE_URL}/admin/stats`);
            const statsData = await statsRes.json();
            setStats(statsData);

            // Fetch users
            const usersRes = await fetchWithAuth(`${API_BASE_URL}/admin/users`);
            const usersData = await usersRes.json();
            setUsers(usersData.users || []);

            // Fetch works
            const worksRes = await fetchWithAuth(`${API_BASE_URL}/admin/works`);
            const worksData = await worksRes.json();
            setWorks(worksData.recentWorks || []);

            // Fetch payments
            const paymentsRes = await fetchWithAuth(`${API_BASE_URL}/admin/payments`);
            const paymentsData = await paymentsRes.json();
            setPayments(paymentsData.payments || []);
            setPaymentStats(paymentsData.stats || null);

        } catch (error) {
            console.error('❌ Errore caricamento dati admin:', error);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'success';
            case 'in_progress': return 'warning';
            case 'pending': return 'default';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography variant="h4" gutterBottom fontWeight={600}>
                        Dashboard Amministratore
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Panoramica completa del sistema
                    </Typography>
                </Box>
                <Tooltip title="Aggiorna dati">
                    <IconButton 
                        onClick={fetchAdminData}
                        color="primary"
                        size="large"
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ mb: 4 }} />

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Utenti Totali
                                    </Typography>
                                    <Typography variant="h4" fontWeight={600}>
                                        {stats?.totalUsers || 0}
                                    </Typography>
                                </Box>
                                <PeopleIcon sx={{ fontSize: 48, color: '#1976d2', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Pratiche Totali
                                    </Typography>
                                    <Typography variant="h4" fontWeight={600}>
                                        {stats?.totalWorks || 0}
                                    </Typography>
                                </Box>
                                <WorkIcon sx={{ fontSize: 48, color: '#2e7d32', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Attività Registrate
                                    </Typography>
                                    <Typography variant="h4" fontWeight={600}>
                                        {stats?.totalActivities || 0}
                                    </Typography>
                                </Box>
                                <BusinessIcon sx={{ fontSize: 48, color: '#ed6c02', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabs */}
            <Card elevation={2}>
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                    <Tab label="Utenti" />
                    <Tab label="Pratiche Recenti" />
                    <Tab label="Pagamenti" />
                </Tabs>

                <CardContent>
                    {/* Tab Utenti */}
                    {activeTab === 0 && (
                        <TableContainer component={Paper} elevation={0}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Ragione Sociale</strong></TableCell>
                                        <TableCell><strong>Attività</strong></TableCell>
                                        <TableCell><strong>Subscription</strong></TableCell>
                                        <TableCell><strong>Registrato</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user) => (
                                        <TableRow key={user.userId} hover>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>{user.ragione_sociale || '-'}</TableCell>
                                            <TableCell>{user.activitiesCount}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={user.subscriptionStatus || 'N/A'} 
                                                    size="small"
                                                    color={user.subscriptionStatus === 'active' ? 'success' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {new Date(user.createdAt).toLocaleDateString('it-IT')}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Tab Pratiche */}
                    {activeTab === 1 && (
                        <TableContainer component={Paper} elevation={0}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Nome Pratica</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Completamento</strong></TableCell>
                                        <TableCell><strong>Creata</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {works.map((work) => (
                                        <TableRow key={work.id_work} hover>
                                            <TableCell>{work.work_name}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={work.status} 
                                                    size="small"
                                                    color={getStatusColor(work.status)}
                                                />
                                            </TableCell>
                                            <TableCell>{work.completion_percentage}%</TableCell>
                                            <TableCell>
                                                {new Date(work.created_at).toLocaleDateString('it-IT')}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Tab Pagamenti */}
                    {activeTab === 2 && (
                        <>
                            {/* Payment Stats Cards */}
                            {paymentStats && (
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={3}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">
                                                    Pagamenti Totali
                                                </Typography>
                                                <Typography variant="h5" fontWeight={600}>
                                                    {paymentStats.totalPayments}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">
                                                    Revenue Totale
                                                </Typography>
                                                <Typography variant="h5" fontWeight={600} color="success.main">
                                                    €{paymentStats.totalRevenue}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">
                                                    Pagati / Falliti
                                                </Typography>
                                                <Typography variant="h5" fontWeight={600}>
                                                    {paymentStats.paidCount} / {paymentStats.failedCount}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">
                                                    Importo Medio
                                                </Typography>
                                                <Typography variant="h5" fontWeight={600}>
                                                    €{paymentStats.averageAmount}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}

                            {/* Payments Table */}
                            <TableContainer component={Paper} elevation={0}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Payment ID</strong></TableCell>
                                            <TableCell><strong>Utente</strong></TableCell>
                                            <TableCell><strong>Importo</strong></TableCell>
                                            <TableCell><strong>Tipo</strong></TableCell>
                                            <TableCell><strong>Status</strong></TableCell>
                                            <TableCell><strong>Data</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {payments.map((payment) => (
                                            <TableRow key={payment.paymentId} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {payment.paymentId.substring(0, 20)}...
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{payment.userEmail}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        €{payment.amount}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={payment.mode} 
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={payment.stripeStatus} 
                                                        size="small"
                                                        color={payment.stripeStatus === 'paid' ? 'success' : 'error'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(payment.paidAt).toLocaleDateString('it-IT')} {' '}
                                                    {new Date(payment.paidAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};
